<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ policy.name || '-' }}
      </portal>
      <card :title="$t('admin.pagePolicy.card.general')" cols="12">
        <!--        <card-badge :tooltip="$t('betaVersion.tooltip')">{{ $t('betaVersion.title') }}</card-badge>-->
        <hm-input v-model="policy.name" name="name" cols="start-1 12 6@desktop" label="Bezeichnung" />
      </card>

      <card :title="$t('admin.pagePolicy.card.cancellationPolicy')" cols="start-1 12 6@desktop">
        <hm-card-inner>
          <hm-input-money :digits="0"
                          v-model="policy.cancellationPolicy.penaltyAfterReservation"
                          :currency="getPenaltyTypeLabel('cancellationPolicy')"
                          :currency-title="getPenaltyTypeDescription('cancellationPolicy')"
                          :currency-clickable="true"
                          :label="$t('admin.pagePolicy.cancellationPolicy.penaltyAfterReservation')"
                          class="number--right"
                          name="penaltyAfterReservation"
                          cols="start-1 12"
                          @currency-click="togglePenaltyType('cancellationPolicy')">
          </hm-input-money>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-number :min-fraction-digits="0"
                           :max-fraction-digits="0"
                           v-model="policy.cancellationPolicy.deadlineDays"
                           :label="$t('admin.pagePolicy.cancellationPolicy.deadlineDays')"
                           required
                           name="deadlineDays"
                           class="number--right"
                           cols="start-1 12"
          />

          <hm-input-time-of-day-slider v-if="policy.cancellationPolicy.deadlineDays === 0"
                                       v-model="policy.cancellationPolicy.deadlineHours"
                                       :step="1"
                                       :convert="false"
                                       :hidden-values="[0, 24]"
                                       :label="$t('admin.pagePolicy.cancellationPolicy.deadlineHours')"
                                       class="text-right"
                                       cols="start-1 12"
                                       name="deadlineHours" />
          <hm-input v-else
                    :label="$t('admin.pagePolicy.cancellationPolicy.deadlineHours')"
                    value="N/A"
                    class="text-right"
                    cols="start-1 12"
                    name="deadlineHours"
                    disabled />
          <hm-input-money :digits="0"
                          v-model="policy.cancellationPolicy.penaltyAfterDeadline"
                          :currency="getPenaltyTypeLabel('cancellationPolicy')"
                          :currency-title="getPenaltyTypeDescription('cancellationPolicy')"
                          :currency-clickable="true"
                          :label="$t('admin.pagePolicy.cancellationPolicy.penaltyAfterDeadline')"
                          class="number--right"
                          name="penaltyAfterDeadline"
                          cols="start-1 12"
                          @currency-click="togglePenaltyType('cancellationPolicy')" />
          <hm-input-toggle v-if="['Percentage', 'Nights'].includes(policy.cancellationPolicy.penaltyType)"
                           v-model="policy.cancellationPolicy.includeExclusiveTaxes"
                           :label="$t('admin.pagePolicy.includeExclusiveTaxes')"
                           :help="$t('admin.pagePolicy.includeExclusiveTaxes_help')"
                           name="includeExclusiveTaxes"
                           cols="start-1 12"
          />
        </hm-card-inner>
      </card>

      <tabable-card cols="12 6@desktop start-7@desktop ">
        <tabable-card-content v-for="lang in [hotelSettings.details.primaryLanguage, ...hotelSettings.details.additionalLanguages]" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner :title="$t('admin.pagePolicy.card.cancellationPolicy')">
            <hm-textarea :value="policy.cancellationPolicy.humanReadable[lang]"
                         :name="`cancellationPolicy-humanReadable-${lang}`"
                         :label="$t('admin.pagePolicy.card.cancellationPolicy')"
                         disabled="disabled"/>
          </hm-card-inner>
          <hm-card-inner v-if="lang === hotelSettings.details.primaryLanguage || !isEmpty(policy.cancellationPolicy.description[lang])"
                         :title="$t('admin.pagePolicy.card.customCancellationPolicy')"
          >
            <card-badge :tooltip="$t('betaVersion.tooltip')">{{ $t('betaVersion.title') }}</card-badge>
            <hm-input-toggle v-model="customCancellationPolicy"
                             :disabled="!hasRole('ROLE_SYSADMIN')"
                             :label="$t('admin.pagePolicy.customCancellationPolicy')"/>
            <template v-if="customCancellationPolicy">
              <hm-textarea v-model="policy.cancellationPolicy.description[lang]"
                           :name="`cancellationPolicy-description-${lang}`"
                           :label="$t('admin.pagePolicy.customCancellationPolicyDescription')"
                           :disabled="!hasRole('ROLE_SYSADMIN')" />
              <hm-textarea :value="policy.cancellationPolicy.autoGeneratedDescription[lang]"
                           :name="`cancellationPolicy-autoGeneratedDescription-${lang}`"
                           :label="$t('admin.pagePolicy.autoGeneratedCancellationPolicyDescription')"
                           :disabled="true" />
            </template>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card :title="$t('admin.pagePolicy.card.prepaymentPolicy')" cols="start-1 12 6@desktop">
        <hm-card-inner>
          <hm-input-money :digits="0"
                          v-model="policy.prepaymentPolicy.penaltyAfterReservation"
                          :currency="getPenaltyTypeLabel('prepaymentPolicy')"
                          :currency-title="getPenaltyTypeDescription('prepaymentPolicy')"
                          :currency-clickable="true"
                          :label="$t('admin.pagePolicy.prepaymentPolicy.penaltyAfterReservation')"
                          class="number--right"
                          name="penaltyAfterReservation"
                          cols="start-1 12"
                          @currency-click="togglePenaltyType('prepaymentPolicy')">
          </hm-input-money>
          <hm-input-toggle v-if="['Percentage', 'Nights'].includes(policy.prepaymentPolicy.penaltyType)"
                           v-model="policy.prepaymentPolicy.includeExclusiveTaxes"
                           :label="$t('admin.pagePolicy.includeExclusiveTaxes')"
                           :help="$t('admin.pagePolicy.includeExclusiveTaxes_help')"
                           name="includeExclusiveTaxes"
                           cols="start-1 12"
          />
        </hm-card-inner>
        <hm-card-inner v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                       :title="$t('admin.pagePolicy.card.additionalDepositPolicySettings')">
          <span class="info-text col-12" v-html="$t('admin.pagePolicy.textInfoAdditonalDepositPolicySettings')" />
          <card-badge :tooltip="$t('betaVersion.tooltip')">{{ $t('betaVersion.title') }}</card-badge>
          <hm-input-number :min-fraction-digits="0"
                           :max-fraction-digits="0"
                           v-model="policy.prepaymentPolicy.deadlineDays"
                           name="deadlineDays"
                           class="number--right"
                           label="Frist (Tage) vor Anreise"
                           cols="start-1 12"
          />

          <hm-input-time-of-day-slider v-if="policy.prepaymentPolicy.deadlineDays === 0"
                                       v-model="policy.prepaymentPolicy.deadlineHours"
                                       :step="1"
                                       :convert="false"
                                       :hidden-values="[0, 24]"
                                       class="text-right"
                                       label="Frist: Uhrzeit am Anreisetag"
                                       cols="start-1 12"
                                       name="deadlineHours" />
          <hm-input v-else
                    value="N/A"
                    class="text-right"
                    label="Frist: Uhrzeit am Anreisetag"
                    cols="start-1 12"
                    name="deadlineHours"
                    disabled />
          <hm-input-money :digits="0"
                          v-model="policy.prepaymentPolicy.penaltyAfterDeadline"
                          :currency="getPenaltyTypeLabel('prepaymentPolicy')"
                          :currency-title="getPenaltyTypeDescription('prepaymentPolicy')"
                          :currency-clickable="true"
                          class="number--right"
                          name="penaltyAfterDeadline"
                          label="Zusätzliche Anzahlung nach Fristablauf"
                          cols="start-1 12"
                          @currency-click="togglePenaltyType('prepaymentPolicy')" />
          <hm-input-toggle v-if="['Percentage', 'Nights'].includes(policy.prepaymentPolicy.penaltyType)"
                           v-model="policy.prepaymentPolicy.includeExclusiveTaxes"
                           :label="$t('admin.pagePolicy.includeExclusiveTaxes')"
                           :help="$t('admin.pagePolicy.includeExclusiveTaxes_help')"
                           name="includeExclusiveTaxes"
                           cols="start-1 12"
          />
        </hm-card-inner>
      </card>


      <tabable-card cols="12 6@desktop start-7@desktop ">
        <tabable-card-content v-for="lang in [hotelSettings.details.primaryLanguage, ...hotelSettings.details.additionalLanguages]" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner :title="$t('admin.pagePolicy.card.prepaymentPolicy')">
            <hm-textarea :value="policy.prepaymentPolicy.humanReadable[lang]"
                         :name="`prepaymentPolicy-humanReadable-${lang}`"
                         :label="$t('admin.pagePolicy.card.prepaymentPolicy')"
                         disabled="disabled"/>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card :title="$t('admin.pagePolicy.card.noShowPolicy')" cols="start-1 12 6@desktop">
        <hm-card-inner>
          <hm-input-money :digits="0"
                          v-model="policy.noShowPolicy.penalty"
                          :currency="getPenaltyTypeLabel('noShowPolicy')"
                          :currency-title="getPenaltyTypeDescription('noShowPolicy')"
                          :currency-clickable="true"
                          :label="$t('admin.pagePolicy.noShowPolicy.penalty')"
                          class="number--right"
                          name="penalty"
                          cols="start-1 12"
                          @currency-click="togglePenaltyType('noShowPolicy')">
          </hm-input-money>
          <hm-input-toggle v-if="['Percentage', 'Nights'].includes(policy.noShowPolicy.penaltyType)"
                           v-model="policy.noShowPolicy.includeExclusiveTaxes"
                           :label="$t('admin.pagePolicy.includeExclusiveTaxes')"
                           :help="$t('admin.pagePolicy.includeExclusiveTaxes_help')"
                           name="includeExclusiveTaxes"
                           cols="start-1 12"
          />
        </hm-card-inner>
      </card>

      <tabable-card cols="12 6@desktop start-7@desktop ">
        <tabable-card-content v-for="lang in [hotelSettings.details.primaryLanguage, ...hotelSettings.details.additionalLanguages]" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner :title="$t('admin.pagePolicy.card.noShowPolicy')">
            <hm-textarea :value="policy.noShowPolicy.humanReadable[lang]"
                         :name="`noShowPolicy-humanReadable-${lang}`"
                         :label="$t('admin.pagePolicy.card.noShowPolicy')"
                         disabled="disabled"/>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card :title="$t('admin.pagePolicy.card.guaranteePolicy')" cols="start-1 12 6@desktop">
        <hm-card-inner>
          <hm-input-select ref="guaranteePolicyType"
                           v-model="policy.guaranteePolicyType"
                           :label="$t('admin.pagePolicy.guaranteePolicyType')"
                           :help="$t('admin.pagePolicy.guaranteePolicyType_help')"
                           name="guaranteePolicyType"
                           enum-name="GuaranteePolicyType" />
        </hm-card-inner>
      </card>

      <card v-if="!canDelete" :title="$t('admin.pagePolicy.card.usage')" cols="12" card-type="collapsible">
        <hm-card-inner v-if="!isEmpty(usedRatePlans)" :full-size="true">
          <hm-col cols="12">
            <template v-for="it in usedRatePlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToRatePlan(it)" />
            </template>
            <template v-if="policy.id === hotelSettings.details.defaultPolicyId">
              <hm-card-inner-line :title="$t('admin.pageHotelSettings.defaultPolicyId')" :clickable="true" @click="$router.push({name: 'hotelSettings'})" />
            </template>
          </hm-col>
        </hm-card-inner>
      </card>

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!policy.deleted && canDelete"
                            :label="$t('admin.pagePolicy.actions.deletePolicy')"
                            icon="delete"
                            color="error"
                            @click="deletePolicy(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import HmInputNumber from '../../lib/inputs/HmInputNumber.vue';
import HmInputMoney from '../../lib/inputs/HmInputMoney.vue';
import HmInputTimeOfDaySlider from '../../lib/inputs/HmInputTimeOfDaySlider.vue';
import TabableCard from '../../lib/layout/TabableCard.vue';
import TabableCardContent from '../../lib/layout/TabableCardContent.vue';
import HmTextarea from '../../lib/inputs/HmTextarea.vue';
import HmInputToggle from '../../lib/inputs/HmInputToggle.vue';
import HmCardInnerInner from '../../lib/layout/HmCardInnerInner.vue';

export default {
  name: 'PagePolicy',
  components: { HmCardInnerInner, HmInputToggle, HmTextarea, TabableCardContent, TabableCard, HmInputTimeOfDaySlider, HmInputMoney, HmInputNumber, HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'policy',
      variable: 'policy',
      event: 'hotelSettings.update',
      exclude: ['cancellationPolicy.humanReadable', 'prepaymentPolicy.humanReadable', 'noShowPolicy.humanReadable'],
      fn: function (cbSaveComplete) {
        if (this.policy.deleted === true) {
          cbSaveComplete();
          return;
        }

        const toSave = cloneDeep(this.policy);

        this.axios.put(this.serviceUrls.policy.get(this, this.policyId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.policy.cancellationPolicy.humanReadable = response.data.data.cancellationPolicy.humanReadable;
            this.policy.prepaymentPolicy.humanReadable = response.data.data.prepaymentPolicy.humanReadable;
            this.policy.noShowPolicy.humanReadable = response.data.data.noShowPolicy.humanReadable;

            this.$serviceBus.$emit('policy.list');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      policy: null,
      policyId: this.$route.params.policyId,
      customCancellationPolicy: false,
      dataLoaded: false,
    };
  },

  computed: {
    usedRatePlans: function () {
      return this.hotelSettings.ratePlans.filter(it => !it.deleted && !isNil(it.policyId) && it.policyId === this.policy.id);
    },
    canDelete: function () {
      if (isNil(this.policy)) return false;
      if (isNil(this.hotelSettings)) return false;
      return this.usedRatePlans.length === 0 && this.policy.id !== this.hotelSettings.details.defaultPolicyId;
    },
  },

  watch: {
    policy: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
    customCancellationPolicy: function (newVal) {
      // console.log(this.policy.cancellationPolicy.description[this.hotelSettings.details.primaryLanguage]);
      // console.log(isEmpty(this.policy.cancellationPolicy.description[this.hotelSettings.details.primaryLanguage]));
      // console.log(newVal);
      if (newVal === true && isEmpty(this.policy.cancellationPolicy.description[this.hotelSettings.details.primaryLanguage])) {
        // console.log('SET');
        this.policy.cancellationPolicy.description[this.hotelSettings.details.primaryLanguage] = this.policy.cancellationPolicy.autoGeneratedDescription[this.hotelSettings.details.primaryLanguage];
      } else if (newVal === false) {
        // console.log('CLEAR');
        this.policy.cancellationPolicy.description[this.hotelSettings.details.primaryLanguage] = null;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('policy.list');
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
    loadData() {
      this.axios.get(this.serviceUrls.policy.get(this, this.policyId))
        .then((response) => {
          if (this.dataLoaded === false) {
            this.policy = response.data.data;
            this.customCancellationPolicy = !isEmpty(this.policy.cancellationPolicy.description[this.hotelSettings.details.primaryLanguage]);
            this.dataLoaded = true;
          }
        });
    },
    deletePolicy(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.policy.get(this, this.policyId))
            .then((response) => {
              this.dataLoaded = false;
              this.policy = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('policy.list');
              this.$router.push({
                name: 'policies',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pagePolicy.confirmDeletePolicy'),
            (doNotShowAgain) => {
              this.deletePolicy(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pagePolicy.policyInUse'));
      }
    },
    getPenaltyTypeLabel(penaltyType) {
      switch (this.policy[penaltyType].penaltyType) {
        case 'Percentage':
          return '%';
        case 'Nights':
          return '<i class="fa fa-moon-o"/>';
        case 'Amount':
          return this.hotelSettings.details.currency;
        default:
          return '?';
      }
    },
    getPenaltyTypeDescription(penaltyType) {
      return this.$t(`PenaltyType.${this.policy[penaltyType].penaltyType}`);
    },
    togglePenaltyType(penaltyType) {
      switch (this.policy[penaltyType].penaltyType) {
        case 'Percentage':
          this.policy[penaltyType].penaltyType = 'Nights';
          break;
        case 'Nights':
          this.policy[penaltyType].penaltyType = 'Amount';
          break;
        case 'Amount':
        default:
          this.policy[penaltyType].penaltyType = 'Percentage';
          break;
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PagePolicy.vue